import { HttpParams } from '@angular/common/http';
import { ApplicationHttpClient } from '@app/helpers/custom-http-client';
import { Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import {
  HttpResult,
  HttpResultWithPagination,
} from '@app/shared/models/http-result';
import { map } from 'rxjs/operators';
import { GeoKLocation, NerbyPlaces } from '@app/shared/models/geo-k-location';
import { TeamMember } from '@app/shared/models/teamMember';
import { HourType } from '@app/shared/models/hour-type';
import { LocationMenuData } from '@app/shared/models/location-menu';
import { AllMedia, Media } from '../models/media';
import {
  BulkUpdateJob,
  BulkUpdateJobDetails,
} from '@app/modules/locations/models/bulk-update-job.interface';

@Injectable()
export class LocationService {
  private locationsUrl = '/locations';

  constructor(private http: ApplicationHttpClient) { }

  public getLocation(id: string): Observable<GeoKLocation> {
    const getLocationUrl = this.locationsUrl + '/' + id;
    return this.http.get<HttpResult>(getLocationUrl).pipe(map((it) => it.data));
  }

  public getMenu(locationId: string): Observable<LocationMenuData> {
    return this.http
      .get<HttpResult>(this.locationsUrl + '/' + locationId + '/menu')
      .pipe(map((it) => it.data));
  }

  public updateMenu(
    locationId: string,
    locationMenuData: LocationMenuData
  ): Observable<LocationMenuData> {
    const updateLocationUrl = this.locationsUrl + '/' + locationId + '/menu';
    return this.http
      .put<HttpResult>(updateLocationUrl, { menu: locationMenuData })
      .pipe(map((it) => it.data));
  }
  batchUpdateMenu(locationMenuData: LocationMenuData, locationIds: string[]) {
    const locationBatch = this.locationsUrl + '/menu';
    return this.http
      .put<HttpResult>(locationBatch, {
        menu: locationMenuData,
        locationIds: locationIds,
      })
      .pipe(map((it) => it.data));
  }

  public updateLocation(
    id: string,
    locationProperty: NonNullable<unknown>
  ): Observable<GeoKLocation> {
    const updateLocationUrl = this.locationsUrl + '/' + id;
    return this.http
      .put<HttpResult>(updateLocationUrl, locationProperty)
      .pipe(map((it) => it.data));
  }

  public getNerbyPlaces(
    id: string,
    lat: number,
    long: number,
    keywords: string,
    radius: number
  ): Observable<NerbyPlaces[]> {
    let nerbyPlacesUrl = this.locationsUrl + '/' + id;
    nerbyPlacesUrl +=
      '/located-in-search?keywords=' +
      keywords +
      '&radius=' +
      radius +
      '&latitude=' +
      lat +
      '&longitude=' +
      long;

    return this.http.get<HttpResult>(nerbyPlacesUrl).pipe(
      map((it) => {
        return it.data;
      })
    );
  }

  public updateLocationTags(
    id: string,
    tags: string[]
  ): Observable<GeoKLocation> {
    const updateLocationTags = this.locationsUrl + '/' + id + '/tags';
    return this.http
      .post<HttpResult>(updateLocationTags, { tagIds: tags })
      .pipe(map((it) => it.data));
  }

  public getActiveMembers(id: string): Observable<TeamMember[]> {
    const filters = [
      {
        property: 'emailVerified',
        operation: 'eq',
        value: true,
      },
      {
        property: 'status',
        operation: 'eq',
        value: 'ACTIVE',
      },
    ];
    const getUsersUrl = `${this.locationsUrl
      }/${id}/members?filter=${JSON.stringify(filters)}`;
    return this.http.get<HttpResult>(getUsersUrl).pipe(
      map((it) => {
        return it.data;
      })
    );
  }

  batchUpdate(locationProperty: NonNullable<unknown>, locationIds: string[]) {
    const locationBatch = this.locationsUrl + '/batch-update-v2';
    return this.http
      .post<HttpResult>(locationBatch, {
        locationData: locationProperty,
        locationIds: locationIds,
      })
      .pipe(map((it) => it.data));
  }

  getBatchUpdateJobs(params?: {
    page?: number;
    limit?: number;
    status?: string;
    user_id?: string;
    from_date?: string;
    to_date?: string;
    category?: string;
    location_ids?: string[];
  }): Observable<{ jobs: BulkUpdateJob[]; totalCount: number; page: number }> {
    const queryParams: string[] = [];
    if (params?.page) queryParams.push(`page=${params.page}`);
    if (params?.limit) queryParams.push(`limit=${params.limit}`);
    if (params?.status) queryParams.push(`status=${params.status}`);
    if (params?.user_id) queryParams.push(`user_id=${encodeURIComponent(params.user_id)}`);
    if (params?.from_date) queryParams.push(`from_date=${params.from_date}`);
    if (params?.to_date) queryParams.push(`to_date=${params.to_date}`);
    if (params?.category) queryParams.push(`category=${encodeURIComponent(params.category)}`);
    if (params?.location_ids?.length) {
      params.location_ids.forEach((id) => queryParams.push(`location_ids=${encodeURIComponent(id)}`));
    }
    const url = `${this.locationsUrl}/batch-update/jobs${queryParams.length ? '?' + queryParams.join('&') : ''}`;
    return this.http.get<HttpResultWithPagination>(url).pipe(
      map((it) => ({
        jobs: (it.data as any)?.jobs ?? [],
        totalCount: it.totalCount ?? 0,
        page: it.page ?? 1,
      }))
    );
  }

  getBatchUpdateJobDetails(jobId: string): Observable<BulkUpdateJobDetails> {
    const url = `${this.locationsUrl}/batch-update/${jobId}`;
    return this.http.get<HttpResult>(url).pipe(map((it) => it.data));
  }

  exportBatchUpdateJobExcel(jobId: string): Observable<Blob | null> {
    const url = `${this.locationsUrl}/batch-update/${encodeURIComponent(jobId)}/export-excel`;
    return this.http.getBlub(url).pipe(map((it) => it.body));
  }

  exportCSV(locationIds: string[]): Observable<Blob | null> {
    const exportCSVUrl = this.locationsUrl + '/export-excel';
    return this.http
      .postBlub(exportCSVUrl, { locationIds })
      .pipe(map((it) => it.body));
  }

  exportActivitiesCSV(locationIds: string[]): Observable<Blob | null> {
    const exportActivitiesCSVUrl = '/location-changes/export-excel';
    return this.http
      .postBlub(exportActivitiesCSVUrl, { locationIds })
      .pipe(map((it) => it.body));
  }

  getHourTypes(id: string): Observable<HourType[]> {
    const getHourTypesUrl = `${this.locationsUrl}/${id}/hour-types`;
    return this.http.get<HttpResult>(getHourTypesUrl).pipe(
      map((it) => {
        return it.data;
      })
    );
  }

  getMedia(
    id: string,
    category: string,
    page: number,
    limit: number
  ): Observable<AllMedia> {
    const getMediaUrl = `${this.locationsUrl}/${id}/media?category=${category}&page=${page}&limit=${limit}`;
    return this.http.get<HttpResultWithPagination>(getMediaUrl).pipe(
      map((it: HttpResultWithPagination) => {
        const allMedia = (it.data || []) as any[];
        return {
          media: allMedia,
          totalCount: it.totalCount || 0,
          nextPageToken: '',
        };
      })
    );
  }

  getMediaCustomer(
    id: string,
    nextPageToken: string,
    limit: number
  ): Observable<AllMedia> {
    const getMediaUrl = `${this.locationsUrl}/${id}/customers-media?pageSize=${limit}&nextPageToken=${nextPageToken}`;
    return this.http.get<HttpResultWithPagination>(getMediaUrl).pipe(
      map((it: HttpResultWithPagination) => {
        const allMedia = (it.data || []) as any[];
        return {
          media: allMedia,
          totalCount: it.totalCount || 0,
          nextPageToken: it.nextPageToken || '',
        };
      })
    );
  }

  postMedia(
    locationId: string,
    url: string,
    category: string,
    format: 'PHOTO' | 'VIDEO' | '' = ''
  ): Observable<Media> {
    const postMediaUrl = `${this.locationsUrl}/${locationId}/media`;
    return this.http
      .post<HttpResult>(postMediaUrl, {
        category: category,
        format: format,
        url: url,
      })
      .pipe(map((it) => it.data));
  }

  deleteMedia(locationId: string, mediaId: string): Observable<void> {
    const deleteMediaUrl = `${this.locationsUrl}/${locationId}/media/${mediaId}`;
    return this.http
      .delete<HttpResult>(deleteMediaUrl)
      .pipe(map((it) => it.data));
  }

  updateTagLocations(tagId: string, locationIds: string[]): Observable<string> {
    const updateTagLocationsUrl = `${this.locationsUrl}/tags`;
    return this.http
      .post<HttpResult>(updateTagLocationsUrl, {
        locationIds: locationIds,
        tagId: tagId,
      })
      .pipe(map((it) => it.data));
  }

  updateChat(
    locationId: string,
    chat: { chatLinks: GeoKLocation['chatLinks'] }
  ): Observable<GeoKLocation> {
    const updateChatUrl = `${this.locationsUrl}/${locationId}`;
    return this.http
      .put<HttpResult>(updateChatUrl, chat)
      .pipe(map((it) => it.data));
  }

  getAiDescription(locationId: string): Observable<string> {
    const getAiDescriptionUrl = `${this.locationsUrl}/${locationId}/ai-description`;
    return this.http
      .get<HttpResult>(getAiDescriptionUrl)
      .pipe(map((it) => it.data));
  }

  getWebsiteUri(gbpLocationIds: string[]): Observable<string> {
    let websiteUriUrl = `${this.locationsUrl}/website-uri`;
    const queryParams = gbpLocationIds
      .map((id) => `gbp_location_id=${encodeURIComponent(id)}`)
      .join('&');
    if (queryParams) {
      websiteUriUrl += `?${queryParams}`;
    }
    return this.http.get<HttpResult>(websiteUriUrl).pipe(map((it) => it.data));
  }

  exportSearchViewsExcel(params?: {
    fromDate?: string;
    toDate?: string;
    locationIDs?: string[];
  }): Observable<HttpResult> {
    const url = `${this.locationsUrl}/search-views/export-excel`;
    const payload: any = {};

    if (params?.fromDate) {
      payload.fromDate = params.fromDate;
    }
    if (params?.toDate) {
      payload.toDate = params.toDate;
    }
    if (params?.locationIDs?.length) {
      payload.locationIDs = params.locationIDs;
    }

    return this.http.post<HttpResult>(url, payload);
  }

  exportKeywordsExcel(params?: {
    fromDate?: string;
    toDate?: string;
    locationIDs?: string[];
  }): Observable<HttpResult> {
    const url = `${this.locationsUrl}/keywords/export-excel`;
    const payload: any = {};

    if (params?.fromDate) {
      payload.fromDate = params.fromDate;
    }
    if (params?.toDate) {
      payload.toDate = params.toDate;
    }
    if (params?.locationIDs?.length) {
      payload.locationIDs = params.locationIDs;
    }

    return this.http.post<HttpResult>(url, payload);
  }

  exportInteractionsExcel(params?: {
    fromDate?: string;
    toDate?: string;
    locationIDs?: string[];
  }): Observable<HttpResult> {
    const url = `${this.locationsUrl}/interactions/export-excel`;
    const payload: any = {};

    if (params?.fromDate) {
      payload.fromDate = params.fromDate;
    }
    if (params?.toDate) {
      payload.toDate = params.toDate;
    }
    if (params?.locationIDs?.length) {
      payload.locationIDs = params.locationIDs;
    }

    return this.http.post<HttpResult>(url, payload);
  }
}
